import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Form, InputGroup, ListGroup, Spinner, Button } from "react-bootstrap";
import DataApi from "../../api/DataApi";

const LookupLayout = ({
  objectName,
  searchFields,
  displayFields,
  value,
  defaultLabel,
  onChange,
  disabled,
  placeholder = "Search...",
  isRequired = false,
  lookupFilter = null, // Array of filter objects: [{field, operator, value}]
  lookupFilterLogic = 'AND', // AND or OR logic for combining filters
}) => {


  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(
    value && defaultLabel ? { id: value, label: defaultLabel } : null
  );
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownIdRef = useRef(`lookup-dropdown-${objectName}-${Date.now()}-${Math.random()}`);

  const searchKeys = searchFields?.split(",").map((f) => f.trim()) || [];
  const displayKeys = displayFields
    ? displayFields.split(",").map((f) => f.trim())
    : searchKeys;

  // Update selected state when value or defaultLabel props change
  useEffect(() => {
    if (value && defaultLabel) {
      setSelected({ id: value, label: defaultLabel });
    } else if (!value) {
      setSelected(null);
    }
  }, [value, defaultLabel]);

  // Calculate dropdown position when it should be shown
  useEffect(() => {
    if (showDropdown && suggestions.length > 0 && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown, suggestions.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        // Check if click is on the dropdown portal
        const dropdownElement = document.getElementById(dropdownIdRef.current);
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    };

    // Use a small delay to ensure the portal is rendered
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 2 || selected) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const dataApi = new DataApi(`object/lookup/${objectName}`);

        // Build search params
        const searchParams = {
          q: searchTerm,
          fields: searchFields,
          display_fields: displayFields,
        };

        // Convert lookup_filter array to filters array format with logic
        let filtersArray = [];
        console.log('LookupLayout - lookupFilter:', lookupFilter);
        console.log('LookupLayout - lookupFilterLogic:', lookupFilterLogic);
        if (lookupFilter && Array.isArray(lookupFilter) && lookupFilter.length > 0) {
          filtersArray = lookupFilter
            .filter(filter => filter.field && filter.operator && filter.value !== undefined && filter.value !== null && filter.value !== '')
            .map(filter => {
              // Handle operators that support multiple values (in, not_in)
              let filterValue = filter.value;
              if (filter.operator === 'in' || filter.operator === 'not_in') {
                // Split comma-separated values
                const values = String(filter.value).split(',').map(v => v.trim()).filter(Boolean);
                filterValue = values.length === 1 ? values[0] : values;
              }
              return {
                field: filter.field,
                operator: filter.operator,
                value: filterValue
              };
            });
          console.log('LookupLayout - filtersArray after processing:', filtersArray);
        }

        // Add filter for active roles only when objectName is 'role' (if no custom filters)
        if (filtersArray.length === 0 && objectName === 'role') {
          filtersArray = [{ field: 'is_active', operator: 'equals', value: 'true' }];
        }

        // Add filters to search params if any exist
        if (filtersArray.length > 0) {
          searchParams.filters = JSON.stringify(filtersArray);
          // Add filter logic (AND or OR)
          if (lookupFilterLogic && (lookupFilterLogic.toUpperCase() === 'OR' || lookupFilterLogic.toUpperCase() === 'AND')) {
            searchParams.filterLogic = lookupFilterLogic.toUpperCase();
          }
          console.log('LookupLayout - searchParams with filters:', searchParams);
        } else {
          console.log('LookupLayout - No filters to add, filtersArray is empty');
        }

        const res = await dataApi.search(searchParams);

        const dataWithLabels = (res.data || []).map((item) => ({
          ...item,
          label: displayKeys
            .map((key) => item[key]?.trim?.() || "")
            .filter(Boolean)
            .join(" "),
        }));

        setSuggestions(dataWithLabels);
      } catch (err) {
        console.error("Lookup error", err);
      } finally {
        setLoading(false);
        setShowDropdown(true);
      }
    };

    const delayDebounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, objectName, searchFields, displayFields, lookupFilter, lookupFilterLogic]);

  const handleSelect = (item) => {
    setSelected(item);
    setSearchTerm("");
    setShowDropdown(false);
    onChange(item.id, item.label);
  };

  const handleClear = () => {
    setSelected(null);
    onChange(null, "");
  };

  return (
    <div style={{ position: "relative" }} ref={inputRef}>
      <InputGroup>
        {selected ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              padding: "6px 12px",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selected.label}
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={handleClear}
              style={{
                color: "#000",
                textDecoration: "none",
                fontSize: "large",
                padding: "0px",
                margin: "0px",
              }}
              disabled={disabled}
            >
              ×
            </Button>
          </div>
        ) : (
          <>
            {value ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  minHeight: "40px",
                  backgroundColor: "#f8f9fa",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ flex: 1, fontWeight: 500 }}>{searchTerm}</span>

                <span
                  onClick={() => {
                    onChange(null, "");
                    setSearchTerm("");
                    setSuggestions([]);
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#dc3545",
                    padding: "0 6px",
                    lineHeight: 1,
                    userSelect: "none",
                  }}
                  title="Clear"
                >
                  &#10005; {/* Unicode for '×' cross */}
                </span>
              </div>
            ) : (
              <Form.Control
                type="text"
                value={searchTerm}
                placeholder={placeholder}
                disabled={disabled}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchTerm.length >= 2) setShowDropdown(true);
                }}
                required={isRequired}
              />
            )}

            {loading && (
              <InputGroup.Text>
                <Spinner animation="border" size="sm" />
              </InputGroup.Text>
            )}
          </>
        )}
      </InputGroup>

      {showDropdown && suggestions.length > 0 && !selected && typeof document !== 'undefined' && createPortal(
        <ListGroup
          id={dropdownIdRef.current}
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999,
            maxHeight: 200,
            overflowY: "auto",
            background: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            borderRadius: "4px",
            border: "1px solid #ccc",
            marginTop: "2px",
          }}
        >
          {suggestions.map((item) => (
            <ListGroup.Item
              action
              key={item.id}
              onClick={() => handleSelect(item)}
              style={{ cursor: "pointer" }}
            >
              {item.label}
            </ListGroup.Item>
          ))}
        </ListGroup>,
        document.body
      )}
    </div>
  );
};

export default LookupLayout;
